import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const AMBER = [180, 83, 9];
const DARK = [41, 37, 36];
const loadColor = (pct) => (pct >= 3 ? [220, 38, 38] : pct >= 2 ? [217, 119, 6] : [22, 163, 74]);
const loadLabel = (pct) => (pct >= 3 ? 'Treat' : pct >= 2 ? 'Watch' : 'OK');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
const nice = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const sectionTitle = (doc, text, y) => {
  doc.setFillColor(...AMBER);
  doc.rect(14, y - 4.5, 2, 6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...DARK);
  doc.text(text, 19, y);
  return y + 4;
};

const statBox = (doc, x, y, w, value, label, color) => {
  doc.setFillColor(255, 251, 235);
  doc.setDrawColor(253, 230, 138);
  doc.roundedRect(x, y, w, 24, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...color);
  doc.text(String(value), x + w / 2, y + 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120, 113, 108);
  doc.text(label, x + w / 2, y + 19, { align: 'center' });
};

export const buildMitePDF = ({ appName, apiaryLabel, summary, activeCount, history, plans, hives, efficacy }) => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const hiveYard = (id, num) => (hives.find(h => h.id === id) || hives.find(h => String(h.hive_number) === String(num)))?.yard || '-';

  // Header band
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, pageW, 30, 'F');
  doc.setTextColor(255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Mite Control Report', 14, 15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`${appName || 'Thrive Beekeeping'}  |  Generated ${fmtDate(new Date())}`, 14, 23);
  doc.setFillColor(255, 255, 255);
  const chip = `Apiary: ${apiaryLabel}`;
  const chipW = doc.getTextWidth(chip) + 8;
  doc.roundedRect(pageW - 14 - chipW, 10, chipW, 9, 4, 4, 'F');
  doc.setTextColor(...AMBER);
  doc.setFont('helvetica', 'bold');
  doc.text(chip, pageW - 14 - chipW / 2, 16, { align: 'center' });

  // Stat boxes
  const latestByHive = {};
  history.forEach(c => { const k = c.hive_id || c.hive_number; if (k && !latestByHive[k]) latestByHive[k] = c; });
  const latest = Object.values(latestByHive);
  const avg = latest.length ? latest.reduce((s, c) => s + (c.percentage || 0), 0) / latest.length : 0;
  const w = (pageW - 28 - 9) / 4;
  statBox(doc, 14, 38, w, latest.length, 'Hives Monitored', DARK);
  statBox(doc, 14 + (w + 3), 38, w, `${avg.toFixed(1)}%`, 'Avg Load (latest per hive)', loadColor(avg));
  statBox(doc, 14 + 2 * (w + 3), 38, w, latest.filter(c => (c.percentage || 0) >= 3).length, 'High Risk Hives (3%+)', [220, 38, 38]);
  statBox(doc, 14 + 3 * (w + 3), 38, w, activeCount, 'Active Treatments', AMBER);

  const tableStyle = {
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2.2, lineColor: [231, 229, 228], textColor: DARK },
    headStyles: { fillColor: AMBER, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [255, 251, 235] },
    margin: { left: 14, right: 14 }
  };
  const statusCell = (pct) => ({ content: loadLabel(pct), styles: { textColor: 255, fillColor: loadColor(pct), fontStyle: 'bold', halign: 'center' } });

  // Apiary overview
  let y = sectionTitle(doc, 'Mite Load by Apiary', 74);
  const byYard = {};
  latest.forEach(c => {
    const yard = hiveYard(c.hive_id, c.hive_number);
    byYard[yard] = byYard[yard] || [];
    byYard[yard].push(c.percentage || 0);
  });
  const yardRows = Object.entries(byYard).map(([yard, vals]) => {
    const a = vals.reduce((s, v) => s + v, 0) / vals.length;
    return [yard, vals.length, `${a.toFixed(1)}%`, vals.filter(v => v >= 3).length, statusCell(a)];
  });
  autoTable(doc, { ...tableStyle, startY: y, head: [['Apiary', 'Hives Tested', 'Avg Load', 'High Risk', 'Status']], body: yardRows.length ? yardRows : [['No mite counts recorded', '', '', '', '']] });

  // Latest per hive
  y = sectionTitle(doc, 'Latest Mite Count per Hive', doc.lastAutoTable.finalY + 12);
  const hiveRows = latest
    .map(c => ({ c, yard: hiveYard(c.hive_id, c.hive_number) }))
    .sort((a, b) => a.yard.localeCompare(b.yard) || (b.c.percentage || 0) - (a.c.percentage || 0))
    .map(({ c, yard }) => [yard, `Hive ${c.hive_number || '?'}`, fmtDate(c.date), nice(c.method), `${c.mite_count || 0} / ${c.bee_count || 300}`, `${(c.percentage || 0).toFixed(1)}%`, statusCell(c.percentage || 0)]);
  autoTable(doc, { ...tableStyle, startY: y, head: [['Apiary', 'Hive', 'Date', 'Method', 'Mites / Bees', 'Load', 'Status']], body: hiveRows.length ? hiveRows : [['-', '-', '-', '-', '-', '-', '-']] });

  // Treatment plans
  y = sectionTitle(doc, 'Treatment Plans', doc.lastAutoTable.finalY + 12);
  const planRows = plans.map(p => {
    const ph = (p.hive_ids || []).map(id => hives.find(h => h.id === id)).filter(Boolean);
    const yards = p.yard_name || [...new Set(ph.map(h => h.yard).filter(Boolean))].join(', ') || '-';
    return [nice(p.treatment_type_name || p.treatment_type), yards, ph.map(h => `#${h.hive_number}`).join(', ') || (p.hive_numbers || []).map(n => `#${n}`).join(', ') || 'All', fmtDate(p.start_date), fmtDate(p.end_date), nice(p.status)];
  });
  autoTable(doc, { ...tableStyle, startY: y, head: [['Treatment', 'Apiary', 'Hives', 'Start', 'End', 'Status']], body: planRows.length ? planRows : [['No treatment plans', '', '', '', '', '']] });

  // Efficacy
  if (efficacy?.length) {
    y = sectionTitle(doc, 'Treatment Efficacy', doc.lastAutoTable.finalY + 12);
    autoTable(doc, {
      ...tableStyle, startY: y,
      head: [['Treatment', 'Uses', 'Hives', 'Avg Before', 'Avg After', 'Avg Reduction', 'Success Rate']],
      body: efficacy.map(e => [nice(e.treatment_type), e.total_treatments, e.total_hives_treated, `${e.avg_pre_mite}%`, `${e.avg_post_mite}%`, `${e.avg_reduction}%`, `${e.success_rate}%`])
    });
  }

  // Footer on every page
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    doc.setDrawColor(231, 229, 228);
    doc.line(14, h - 14, pageW - 14, h - 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(120, 113, 108);
    doc.text('Status: OK under 2%  |  Watch 2-3%  |  Treat 3% or more (varroa per 100 bees)', 14, h - 9);
    doc.text(`Page ${i} of ${pages}`, pageW - 14, h - 9, { align: 'right' });
  }
  return doc;
};
